import React, { useCallback, useMemo, useRef } from 'react';
import { useAppPrintMode } from '../../../print';
import { formatExportableSubjectWithExplicitFee, formatSubjectWithProvidedFee } from '../../../legend';
import { StudioHighchart } from '../../../highchart/Highchart';
import DownloadableContentBlock from '../../../content-block/DownloadableContentBlock';
import { useBlockContext } from '../../contexts/block-context';
import type HighchartsReact from 'highcharts-react-official';
import { useRecoilValue } from 'recoil';
import {
  blockExportMetadata,
  blockFonts,
  blockSelectedCustomizableMetrics,
  blockSubjectHasProxyState,
  customizedBlock,
} from 'venn-state';
import { useGetRequestSubjectFee } from '../../utils/hooks/use-advisory-fee';
import { useTheme } from 'styled-components';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';
import { useSubjectColors } from '../../../studio-blocks/logic/useSubjectColors';
import { returnsDistributionParser as makeReturnsDistributionRowData } from '../../../studio-blocks/logic/parsers';
import { merge } from 'lodash';
import {
  convertReturnDataToExcel,
  getDefaultChartConfig,
  getOptions,
} from '../../../studio-blocks/components/charts/returns-distribution/utils';
import { WATERMARK_POSITION_TOP } from '../../../studio-blocks/customAnalysisContants';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { getAnalyzedSubjectFromRequestSubject } from 'venn-utils';
import {
  useGetBlockSubjectSuperscripts,
  usePageProxySymbol,
} from '../../components/core/block-subject-footnotes-and-superscripts';

export const DistributionContent = ({ id }: { id: string }) => {
  const subjectHasProxy = useRecoilValue(blockSubjectHasProxyState(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const axisFont = useRecoilValue(blockFonts.blockChartAxis(id));
  const customizableMetrics = useRecoilValue(blockSelectedCustomizableMetrics(id));
  const block = useRecoilValue(customizedBlock(id));
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const proxySymbol = usePageProxySymbol();
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const theme = useTheme();
  const { analyses, requests } = useFilteredRequestsAndAnalyses(id);
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { blockRef } = useBlockContext();

  const analyzedSubjects = useMemo(
    () => requests.map(({ subject }) => subject).map(getAnalyzedSubjectFromRequestSubject),
    [requests],
  );
  const subjectColors = useSubjectColors(analyzedSubjects);
  const rowData = makeReturnsDistributionRowData(customizableMetrics)(block, analyses);

  const defaultHeader =
    block?.relativeToBenchmark && customizableMetrics?.[0]?.relativeLabel
      ? customizableMetrics[0].relativeLabel
      : (customizableMetrics?.[0]?.label ?? 'Builder Chart');

  const excelDataFn = useCallback(() => {
    const subjectNames = requests.map((request) =>
      formatExportableSubjectWithExplicitFee(
        request.subject,
        false,
        getRequestSubjectFee({ subject: request.subject, isBenchmark: request.isBenchmark }),
      ),
    );
    const relatives = requests.map(({ relative }) => relative);
    return (
      convertReturnDataToExcel(
        rowData.filter(({ exportable }) => exportable),
        subjectNames,
        relatives,
      ) || []
    );
  }, [requests, rowData, getRequestSubjectFee]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const options = useMemo(
    () =>
      rowData?.length
        ? merge(
            {},
            getDefaultChartConfig(theme, Boolean(inPrintModeOrReportLab)),
            getOptions({
              customBlockType: 'DISTRIBUTION' as const,
              print: inPrintModeOrReportLab,
              percent: rowData[0]?.percent,
              yAxisLabel: rowData[0]?.yAxisLabel ?? '',
              yAxisTitle: rowData[0]?.yAxisTitle,
              xAxisCategories: rowData[0]?.xAxisCategories,
              series: requests.map((request, index) => ({
                type: 'column' as const,
                name: formatSubjectWithProvidedFee(
                  request.subject,
                  proxySymbol,
                  !!subjectHasProxy[request.subject.id],
                  getBlockSubjectSuperscripts({
                    subject: request.subject,
                    isBenchmark: !!request.isBenchmark,
                  }),
                  getRequestSubjectFee({ subject: request.subject, isBenchmark: !!request.isBenchmark }),
                ),
                color: subjectColors[index],
                data: [...(rowData?.[index]?.seriesData || [])],
              })),
              typography: theme.Typography,
              legendFontSize: legendFont.fontSizePt,
              axisFontSize: axisFont.fontSizePt,
            }),
          )
        : undefined,
    [
      rowData,
      theme,
      inPrintModeOrReportLab,
      requests,
      legendFont.fontSizePt,
      axisFont.fontSizePt,
      proxySymbol,
      subjectHasProxy,
      getBlockSubjectSuperscripts,
      getRequestSubjectFee,
      subjectColors,
    ],
  );

  return (
    <DownloadableContentBlock
      header={null}
      noBorder
      floatingOptions
      downloadable={{
        png: true,
        options: {
          fileName: block.header ?? defaultHeader,
          watermark: { top: WATERMARK_POSITION_TOP, right: 20 },
        },
        tracking: {
          description: 'BUILDER',
          relativeToBenchmark: block.relativeToBenchmark,
          userUploaded: false,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: rowData ? rowData.length === 0 : true,
        target: blockRef,
      }}
    >
      {options && <StudioHighchart options={options} blockId={id} chartRef={chartRef} style={{ height: '100%' }} />}
    </DownloadableContentBlock>
  );
};
